/**
 * Stylesheet shared by every printable ticket. Owned by `feature-print` (not derived from the app
 * bundle) so the document remains stable regardless of which global stylesheets the host page
 * loads. Keep this in sync with the structural classes used in
 * `libs/feature/ticket/src/components/print/ticket/upp-print.html` / `upp-print.scss`.
 */
export const TICKET_CSS_STYLE = `
body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    font-family: 'Roboto Condensed', Helvetica, Arial, monospace, sans-serif;
}

.upp-ticket-print {
    font-size: 14px;
    font-weight: bold;
    line-height: 1.3;
}

/* Keep colors/backgrounds in browser print and HTML->PDF renderers (WYSIWYG). */
.upp-ticket-print,
.upp-ticket-print *,
.upp-ticket-print *::before,
.upp-ticket-print *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}

/* Some print engines ignore row background/color; push those styles down to cells. */
.upp-ticket-print tr[style*="background"] > td {
    background-color: inherit !important;
}

.upp-ticket-print tr[style*="color"] > td {
    color: inherit !important;
}

/* Thermal template relies on inline tr styles for key visual sections. */
.upp-ticket-print tr[style*="background: lightgray"] > td,
.upp-ticket-print tr[style*="background: #e8e8e8"] > td,
.upp-ticket-print tr[style*="background:#e8e8e8"] > td {
    background-color: #e8e8e8 !important;
}

.upp-ticket-print tr[style*="background: black"] > td,
.upp-ticket-print tr[style*="background:black"] > td {
    background-color: #000 !important;
}

.upp-ticket-print tr[style*="background: black"][style*="color: white"] > td,
.upp-ticket-print tr[style*="background:black"][style*="color:white"] > td {
    color: #fff !important;
}

.upp-ticket-print .printing {
    position: relative;
    height: fit-content;
    width: 100%;
    z-index: 1;
    opacity: 1;
    display: block;
}

.upp-ticket-print .printing.visible {
    position: relative !important;
    z-index: 1 !important;
    opacity: 1 !important;
}

.upp-ticket-print .printing--thermal {
    width: var(--upp-ticket-thermal-width, 80mm);
    max-width: 100%;
    margin: 0 auto;
}

.upp-ticket-print .printing--a5 {
    width: 100%;
    max-width: 148mm;
    margin: 0 auto;
}

.upp-ticket-print .print-view {
    position: relative;
    display: block;
    text-align: left;
    text-transform: none;
    background: #fff;
    color: #000;
}

.upp-ticket-print .print-view--thermal {
    margin: 0 1px;
    padding: 10px 5px;
    font-family: 'Roboto Condensed', Helvetica, monospace, sans-serif;
    font-weight: bold;
    font-size: 14px;
}

.upp-ticket-print .print-view--a5 {
    box-sizing: border-box;
    width: 100%;
    max-width: 148mm;
    min-height: 210mm;
    margin: 0 auto;
    padding: 10mm 11mm 12mm;
    font-family: 'Roboto Condensed', Helvetica, Arial, sans-serif;
    font-weight: normal;
    font-size: 11px;
    line-height: 1.35;
}

.upp-ticket-print .a5-doc-heading {
    margin-bottom: 4mm;
}

.upp-ticket-print .a5-header-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 6mm;
}

.upp-ticket-print .a5-header-body {
    flex: 1 1 auto;
    min-width: 0;
}

.upp-ticket-print .a5-brand-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 4mm;
}

.upp-ticket-print .a5-place-block {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
    margin-left: auto;
}

.upp-ticket-print .a5-place-line {
    font-size: 12px;
    margin-bottom: 1mm;
}

.upp-ticket-print .a5-place-detail {
    font-size: 10px;
    line-height: 1.4;
}

.upp-ticket-print .a5-meta-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 2mm 4mm;
    margin-top: 2.5mm;
    padding-top: 2mm;
    border-top: 1px solid #ccc;
    font-size: 10px;
}

.upp-ticket-print .a5-meta-location {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: bold;
}

.upp-ticket-print .a5-meta-ticketref {
    flex: 0 1 auto;
    text-align: right;
    font-weight: bold;
    word-break: break-all;
}

.upp-ticket-print .a5-strong {
    font-weight: bold;
}

.upp-ticket-print .a5-logo-box {
    flex: 0 0 auto;
    max-width: 42%;
    max-height: 22mm;
    text-align: left;
}

.upp-ticket-print .a5-logo-box .logo {
    display: block;
    max-height: 22mm;
    max-width: 100%;
    object-fit: contain;
}

.upp-ticket-print .a5-fiscal-qr {
    flex: 0 0 auto;
    text-align: center;
    max-width: 38mm;
}

.upp-ticket-print .a5-qrcode-size {
    height: 32mm;
    width: 32mm;
}

.upp-ticket-print .a5-qrcode-size img {
    width: 32mm !important;
    height: 100%;
}

.upp-ticket-print .a5-qrcode-frame {
    box-sizing: content-box;
    padding: 2mm;
    border: 1px solid #000;
    background: #fff;
}

.upp-ticket-print .a5-section-head {
    width: 100%;
    margin-top: 3mm;
    font-size: 10px;
}

.upp-ticket-print .a5-section-head td {
    background: #e8e8e8;
    padding: 2mm 2mm;
    text-transform: uppercase;
}

.upp-ticket-print .a5-kv {
    width: 100%;
    font-size: 10px;
    margin-top: 1mm;
}

.upp-ticket-print .a5-kv td {
    padding: 1mm 0;
    vertical-align: top;
}

.upp-ticket-print .a5-kv .right {
    text-align: right;
    white-space: nowrap;
}

.upp-ticket-print .a5-invoice-refs {
    table-layout: fixed;
}

.upp-ticket-print .a5-invoice-refs td {
    width: 50%;
    white-space: normal;
}

.upp-ticket-print .a5-invoice-refs td:first-child {
    padding-right: 2.5mm;
}

.upp-ticket-print .a5-invoice-refs td:last-child {
    padding-left: 2.5mm;
}

.upp-ticket-print .a5-invoice-refs .a5-ref-label {
    font-weight: bold;
    margin-right: 1mm;
}

.upp-ticket-print .a5-invoice-refs .a5-ref-cell {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 3mm;
    width: 100%;
}

.upp-ticket-print .a5-invoice-refs .a5-ref-value {
    text-align: right;
    margin-left: auto;
    white-space: nowrap;
}

.upp-ticket-print .a5-block {
    width: 100%;
    font-size: 10px;
    margin-top: 1mm;
    text-align: left;
}

.upp-ticket-print .a5-block td {
    padding: 0.5mm 0;
}

.upp-ticket-print .a5-parties {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 5mm;
    margin-top: 2mm;
}

.upp-ticket-print .a5-party {
    flex: 1 1 0;
    min-width: 0;
}

.upp-ticket-print .a5-party--issuer {
    text-align: left;
}

.upp-ticket-print .a5-party--receiver {
    text-align: right;
}

.upp-ticket-print .a5-party--receiver .a5-section-head td,
.upp-ticket-print .a5-party--receiver .a5-block {
    text-align: right;
}

.upp-ticket-print .a5-order-head {
    width: 100%;
    margin-top: 2mm;
    background: #e8e8e8;
    font-size: 10px;
}

.upp-ticket-print .a5-order-head td {
    padding: 1mm 2mm;
}

.upp-ticket-print .a5-order-head .a5-sub {
    font-size: 9px;
    padding-top: 0.5mm;
    padding-bottom: 0.8mm;
}

.upp-ticket-print .a5-lines {
    width: 100%;
    margin-top: 2mm;
    font-size: 10px;
    border-collapse: collapse;
}

.upp-ticket-print .a5-lines td {
    padding: 1.2mm 1mm;
    border-bottom: 1px solid #ccc;
    vertical-align: top;
}

.upp-ticket-print .a5-lines .a5-subhead td {
    background: #f0f0f0;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid #999;
}

.upp-ticket-print .a5-lines .a5-option {
    font-size: 9px;
    color: #222;
}

.upp-ticket-print .a5-pay-wrap {
    margin-top: 3mm;
}

.upp-ticket-print .a5-pay-flag {
    width: 100%;
    border: 1px solid #000;
    font-size: 10px;
    text-transform: uppercase;
}

.upp-ticket-print .a5-pay-flag td {
    text-align: center;
    padding: 2mm;
}

.upp-ticket-print .a5-totals {
    width: 100%;
    margin-top: 3mm;
    font-size: 10px;
}

.upp-ticket-print .a5-totals td {
    padding: 1mm 0;
}

.upp-ticket-print .a5-totals .a5-indent {
    padding-left: 3mm;
}

.upp-ticket-print .a5-totals .a5-total-row {
    font-weight: bold;
    font-size: 12px;
    border-top: 2px solid #000;
    margin-top: 1mm;
}

.upp-ticket-print .a5-totals .a5-total-row td {
    padding-top: 2mm;
}

.upp-ticket-print .a5-thanks {
    margin-top: 5mm;
    padding-top: 3mm;
    border-top: 1px solid #000;
    text-align: center;
    text-transform: uppercase;
    font-size: 10px;
}

.upp-ticket-print .a5-ticketbai {
    margin-top: 4mm;
}

.upp-ticket-print .a5-footer-spacer {
    height: 8mm;
}

.upp-ticket-print .logo-box {
    height: 25mm;
    width: 100%;
    text-align: center;
    overflow: hidden;
}

.upp-ticket-print .logo-box .logo {
    max-width: 100%;
    height: 100%;
    width: 100%;
    object-fit: contain;
}

.upp-ticket-print .padding {
    padding: 0 5px;
}

.upp-ticket-print table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0 0 0;
}

.upp-ticket-print tr {
    margin: 1px 4px;
    line-height: 1.4em;
}

.upp-ticket-print .strike {
    text-decoration: line-through;
    text-decoration-thickness: 3px;
}

.upp-ticket-print .bold {
    font-size: 110%;
    font-weight: bold;
}

.upp-ticket-print .italic {
    font-style: italic;
}

.upp-ticket-print td {
    height: inherit !important;
    font-size: inherit !important;
    vertical-align: top;
}

.upp-ticket-print td.center {
    text-align: center;
}

.upp-ticket-print td.left {
    text-align: left;
}

.upp-ticket-print td.right {
    padding-left: 10px;
    white-space: nowrap;
    width: 1%;
    text-align: right;
    min-width: fit-content;
}

.upp-ticket-print .qrcode-box {
    margin-top: 5mm;
}

.upp-ticket-print .qrcode-box .qrcode-info {
    font-size: 90%;
    font-weight: normal;
    text-align: center;
    white-space: normal;
}

.upp-ticket-print .qrcode-box .qrcode-code {
    display: block;
    margin: 6mm auto;
    height: 40mm;
    width: 40mm;
    position: relative;
    text-align: center;
}

.upp-ticket-print .qrcode-box .qrcode-code img {
    display: block;
    margin: 0;
    padding: 0;
    height: 100%;
    width: 40mm !important;
    object-fit: contain;
}

.upp-ticket-print .qrcode-box.verifactu {
    margin-bottom: 5mm;
}

.upp-ticket-print .section-head td {
    padding: 0 5px;
}

.upp-ticket-print .section-body td {
    padding: 8px 5px 4px;
}

.upp-ticket-print .merchant-body {
    padding: 8px 5px 4px;
}

.upp-ticket-print .merchant-body--customer {
    padding-bottom: 10px;
}

.upp-ticket-print .section-gap td {
    height: 10px;
    padding: 0;
    line-height: 0;
    font-size: 0;
}

.upp-ticket-print .totals-head td {
    padding: 6px 5px;
}

.upp-ticket-print .totals-body td {
    padding: 8px 5px 2px;
}

.upp-ticket-print .proforma-header-table {
    margin-top: 2px;
}

.upp-ticket-print .proforma-header-table + table {
    margin-top: 2px;
}

.upp-ticket-print .proforma-title {
    text-align: center;
    text-transform: uppercase;
    border: 1px solid #000;
    margin: 1mm 0;
    padding: 2mm;
}

@media print {
    body {
        margin: 0;
    }
}
`.trim();

/**
 * Print page configuration used to build the `@page` rule.
 */
export type StyleFormat = {
    /**
     * Page size for print output.
     * - `number`: thermal width in millimeters (for example `58`, `72`, `80`).
     * - `'A5'`: standard A5 paper format.
     */
    size: number | 'A5';
    /**
     * Margins in millimeters: `[top, left, bottom, right]`.
     */
    margin: [number, number, number, number];
    /**
     * Multiplier for ticket body font sizes. Omit for the default stylesheet (factor 1).
     * Used by the Paytef terminal profile (~1.5).
     */
    scale?: number;
    /**
     * Fiscal QR box size in millimeters. Omit for the default stylesheet (40 mm).
     * TicketBAI requires 30–40 mm; the Paytef terminal profile uses 40 mm.
     */
    qrsize?: number;
};

const DEFAULT_STYLE_FORMAT: StyleFormat = {
    size: 80,
    margin: [0, 0, 0, 0],
};

const DEFAULT_SCALE = 1;
const DEFAULT_QRSIZE_MM = 40;

/**
 * Builds the CSS payload for printable tickets by prepending a computed `@page` rule
 * to the shared ticket stylesheet. When `scale` or `qrsize` is set, appends an override
 * block so thermal width, body fonts and the QR box match the requested profile.
 *
 * @param format Page size and margin configuration. `null` falls back to thermal defaults.
 * @returns Complete CSS string (`@page` + `TICKET_CSS_STYLE` [+ overrides]).
 * @throws {Error} When `size`/`scale`/`qrsize` is invalid or any margin value is not a finite non-negative number.
 */
export const ticketStyle = (format: StyleFormat | null): string => {
    const _format = format || DEFAULT_STYLE_FORMAT;
    const [top, left, bottom, right] = _format.margin;

    if (_format.size !== 'A5' && (!Number.isFinite(_format.size) || _format.size <= 0)) {
        throw new Error('[TICKET STYLE] Invalid format');
    }
    if (!Number.isFinite(top) || top < 0 || !Number.isFinite(left) || left < 0 || !Number.isFinite(bottom) || bottom < 0 || !Number.isFinite(right) || right < 0) {
        throw new Error('[TICKET STYLE] Invalid format');
    }

    const _hasScale = _format.scale !== undefined && _format.scale !== null;
    const _hasQr = _format.qrsize !== undefined && _format.qrsize !== null;
    const _scale = _hasScale ? Number(_format.scale) : DEFAULT_SCALE;
    const _qrsize = _hasQr ? Number(_format.qrsize) : DEFAULT_QRSIZE_MM;
    if (_hasScale || _hasQr) {
        if (!Number.isFinite(_scale) || _scale <= 0 || !Number.isFinite(_qrsize) || _qrsize <= 0) {
            throw new Error('[TICKET STYLE] Invalid format');
        }
    }

    const sizeRule = _format.size === 'A5' ? 'A5' : `${_format.size}mm auto`;
    const pageRule = `@page { size: ${sizeRule}; margin: ${top}mm ${right}mm ${bottom}mm ${left}mm; }`;
    const base = `${pageRule}\n${TICKET_CSS_STYLE}`;

    if (!_hasScale && !_hasQr) {
        return base;
    }

    const _widthMm = _format.size === 'A5' ? 148 : _format.size;
    return `${base}\n${_TicketScaleOverrides(_widthMm, _scale, _qrsize)}`;
};

/**
 * Override block appended after {@link TICKET_CSS_STYLE} so thermal receipts can use a
 * different paper width, larger body type and a larger fiscal QR without changing the
 * default stylesheet used by bar printers and A5.
 */
function _TicketScaleOverrides(widthMm: number, scale: number, qrsizeMm: number): string {
    const _basePx = 14 * scale;
    const _midPx = 12 * scale;
    const _smallPx = 9 * scale;
    return `
.upp-ticket-print {
    --upp-ticket-thermal-width: ${widthMm}mm;
    font-size: ${_basePx}px;
}
.upp-ticket-print .print-view--thermal {
    font-size: ${_basePx}px;
}
.upp-ticket-print .print-view--thermal .ticket-header,
.upp-ticket-print .print-view--thermal .ticket-footer,
.upp-ticket-print .print-view--thermal .section-body,
.upp-ticket-print .print-view--thermal .totals-body {
    font-size: ${_midPx}px;
}
.upp-ticket-print .print-view--thermal .ticket-meta,
.upp-ticket-print .print-view--thermal .fiscal-note {
    font-size: ${_smallPx}px;
}
.upp-ticket-print .qrcode-box .qrcode-code {
    height: ${qrsizeMm}mm;
    width: ${qrsizeMm}mm;
}
.upp-ticket-print .qrcode-box .qrcode-code img {
    width: ${qrsizeMm}mm !important;
    max-width: ${qrsizeMm}mm !important;
    height: auto !important;
}
`.trim();
}
